<template>
  <BlogHeader
    v-if="content"
    :collection="collection"
    :title="content.title"
    :categories="content.categories"
    :author="content.author"
    :cover-image="content.coverImage"
    :date="content.date" />

  <main
    v-if="content"
    class="bg-white">
    <div class="h-12 md:h-24" />

    <template v-for="(element, index) in content.content">
      <BlogContainer
        v-if="element.type === 'text'"
        :key="index"
        class="prose prose-lg font-normal">
        <BlogText :content="element.text" />
      </BlogContainer>

      <BlogContainer
        v-if="element.type === 'lead'"
        :key="index"
        class="">
        <BlogLead :content="element.content" />
      </BlogContainer>

      <template v-if="element.type === 'game_ctas'">
        <BlogGameCta
          v-for="gameCta in element.items"
          :key="gameCta.id"
          v-bind="gameCta" />
      </template>

      <BlogCta
        v-if="element.type === 'cta'"
        :key="index"
        :background-gradient="element.backgroundGradient"
        :title="element.title"
        :app-store="element.appStore ?? ''"
        :google-play="element.googlePlay ?? ''"
        :app-gallery="element.appGallery ?? ''"
        :amazon-appstore="element.amazonAppstore ?? ''"
        :image="element.image"
        :cta="element.cta" />

      <BlogSlider
        v-if="element.type === 'slider'"
        :key="index"
        :slides="element.items" />

      <BlogYoutube
        v-if="element.type === 'youtube'"
        :key="index"
        v-bind="element" />
    </template>

    <BlogShare class="my-12" />

    <Container class="w-full pb-16 pt-3 md:pb-24 md:pt-6">
      <BlogBackLink
        :collection="collection"
        :show-label-on-mobile="true" />
    </Container>
  </main>

  <SectionSliderTopHeading
    v-if="latestItems.length > 0"
    :heading="{ title: readMoreTitle }"
    :cards="latestItems" />
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { createError, queryContent, useAsyncData, useHead } from '#imports'

  import { BlogCollection } from '~/constants'
  import { stripEndingSlash } from '~/utils/urls'

  import routesList from '../content/routes.json'

  type BlogType = {
    collection: BlogCollection
  }

  const props = defineProps<BlogType>()

  const route = useRoute()
  const readMoreTitle =
    props.collection === BlogCollection.STORIES
      ? 'Read more stories'
      : props.collection === BlogCollection.NEWS
        ? 'Read more news'
        : props.collection === BlogCollection.PRESS_RELEASES
          ? 'Read more press releases'
          : props.collection === BlogCollection.TECH
            ? 'Read more tech blog posts'
            : 'Read more'

  const uri = stripEndingSlash(route.path)

  const page = routesList.find(item => item.url === uri)
  let entry_id = ''

  if (!page) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found',
      fatal: true
    })
  } else {
    entry_id = page.id
  }

  const { data } = await useAsyncData(entry_id, () => queryContent(props.collection, entry_id).findOne())

  useHead({
    title: data.value?.meta?.title,
    link: [
      {
        rel: 'canonical',
        href: `https://outfit7.com${route.path}`
      }
    ],
    meta: [
      { hid: 'description', name: 'description', content: data.value?.meta?.description },

      { hid: 'og:title', property: 'og:title', content: data.value?.social?.title },
      // { hid: 'og:url', property: 'og:url', content: this.pageUrl },
      { hid: 'og:description', property: 'og:description', content: data.value?.social?.description },
      {
        hid: 'og:image',
        property: 'og:image',
        content: `https://o7web.com/cdn-cgi/image/w=1200/${data.value?.social?.image}`
      },

      { hid: 'twitter:title', name: 'twitter:title', content: data.value?.social?.title },
      // { hid: 'twitter:url', name: 'twitter:url', content: this.pageUrl },
      { hid: 'twitter:description', name: 'twitter:description', content: data.value?.meta?.description },
      { hid: 'twitter:image', name: 'twitter:image', content: data.value?.social?.image }
    ]
  })

  // Fetch last 3 blog posts from stories
  const latest = await useAsyncData(`latest${entry_id}`, () =>
    queryContent(props.collection)
      .where({ id: { $ne: entry_id } })
      .sort({ date: -1 })
      .limit(3)
      .find()
  )

  // computed content
  const content = computed(() => {
    if (data && data.value) {
      return data.value
    }

    return false
  })

  const latestItems = computed(() => {
    if (latest && latest.data && latest.data.value && latest.data.value.length > 0) {
      return latest.data.value.map(item => item.card)
    }

    return []
  })
</script>
